'use client'

import Link from 'next/link'
import { useInView } from '@/hooks/useInView'
import { useState } from 'react'
import {
  ArrowRight,
  Check,
  X,
  AlertCircle,
  Trophy,
  ChevronDown,
  Shield,
  Zap,
  Users,
  TrendingUp,
  Star,
  Crown,
} from 'lucide-react'
import { BOOK_A_DEMO_URL } from '@/lib/constants'
import { AttributedCtaLink } from '@/components/conversion/AttributedCtaLink'
import {
  BUILDERLYNC_FEATURES,
  FEATURE_LABELS,
  FEATURE_ORDER,
  type Competitor,
  type FeatureKey,
} from '@/data/competitors'
import { RelatedGuides } from '@/components/seo/RelatedGuides'
import { compareLinks } from '@/lib/internal-links'

/* ── Feature Status Icon ──────────────────────────────────── */
function FeatureIcon({ value }: { value: boolean | 'limited' }) {
  if (value === true)
    return (
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-500/15 ring-1 ring-green-500/20">
        <Check size={16} className="text-green-400" />
      </span>
    )
  if (value === 'limited')
    return (
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-500/15 ring-1 ring-yellow-500/20">
        <AlertCircle size={16} className="text-yellow-400" />
      </span>
    )
  return (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-500/10 ring-1 ring-red-500/20">
      <X size={16} className="text-red-400/70" />
    </span>
  )
}

/* ── FAQ Accordion Item ───────────────────────────────────── */
function FAQItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false)
  const { ref, isInView } = useInView()

  return (
    <div
      ref={ref}
      className={`glass-card-premium overflow-hidden anim-fade-in-up anim-delay-${Math.min(index + 1, 8)} ${isInView ? 'in-view' : ''}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-6 text-left cursor-pointer"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-white pr-4">{q}</span>
        <ChevronDown
          size={20}
          className={`text-zinc-400 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: open ? '500px' : '0px',
          opacity: open ? 1 : 0,
        }}
      >
        <p className="px-6 pb-6 text-sm text-zinc-400 leading-relaxed">
          {a}
        </p>
      </div>
    </div>
  )
}

/* ── Main Component ───────────────────────────────────────── */
export default function ComparisonClient({
  competitor,
}: {
  competitor: Competitor
}) {
  // Count features where BuilderLync wins
  const blWins = FEATURE_ORDER.filter(
    (k) => BUILDERLYNC_FEATURES[k] === true && competitor.features[k] !== true
  ).length

  const compFullFeatures = FEATURE_ORDER.filter(
    (k) => competitor.features[k] === true
  ).length

  const whySwitchIcons = [Shield, Zap, Users, TrendingUp, Star, Crown]

  const { ref: heroRef, isInView: heroInView } = useInView()
  const { ref: tableRef, isInView: tableInView } = useInView()
  const { ref: priceRef, isInView: priceInView } = useInView()
  const { ref: whyRef, isInView: whyInView } = useInView()
  const { ref: faqRef, isInView: faqInView } = useInView()
  const { ref: ctaRef, isInView: ctaInView } = useInView()

  return (
    <div className="min-h-screen">
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="section relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 gradient-mesh-hero opacity-60" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-red-600/5 rounded-full blur-[120px]" />

        <div ref={heroRef} className="section-inner text-center relative z-10">
          <div
            className={`anim-fade-in-up ${heroInView ? 'in-view' : ''} inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-red-600/20 bg-red-600/5 backdrop-blur-sm mb-8`}
          >
            <Trophy size={14} className="text-red-500" />
            <span className="text-sm text-red-300/80">Competitor Comparison</span>
          </div>

          <h1
            className={`anim-fade-in-up anim-delay-1 ${heroInView ? 'in-view' : ''} text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-[-0.03em] mb-6`}
          >
            BuilderLync vs{' '}
            <span className="gradient-text-red">{competitor.name}</span>
          </h1>

          <p
            className={`anim-fade-in-up anim-delay-2 ${heroInView ? 'in-view' : ''} text-lg text-zinc-400 max-w-2xl mx-auto mb-10`}
          >
            {competitor.tagline}
          </p>

          <div
            className={`anim-fade-in-up anim-delay-3 ${heroInView ? 'in-view' : ''} flex flex-col sm:flex-row items-center justify-center gap-4`}
          >
            <AttributedCtaLink
              href={BOOK_A_DEMO_URL}
              page="compare"
              cta={`Hero — Book a Demo (${competitor.name})`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-base px-7 py-3.5 glow-red-sm"
            >
              Book a Demo
              <ArrowRight size={18} />
            </AttributedCtaLink>
            <Link href="/pricing" className="btn-ghost text-base px-7 py-3.5">
              Compare Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* ── Feature Comparison Table ─────────────────────────── */}
      <section className="section" ref={tableRef}>
        <div className="section-inner">
          <div
            className={`anim-fade-in-up ${tableInView ? 'in-view' : ''} text-center mb-12`}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              Feature-by-Feature{' '}
              <span className="gradient-text-red">Comparison</span>
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              See exactly where BuilderLync wins — and why contractors are making the
              switch.
            </p>
          </div>

          {/* Score badges */}
          <div
            className={`anim-scale-in anim-delay-1 ${tableInView ? 'in-view' : ''} flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-10`}
          >
            <div className="glass-card-premium px-6 py-3.5 flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]" />
              <span className="text-sm text-zinc-300">
                BuilderLync:{' '}
                <span className="font-bold text-white">
                  {FEATURE_ORDER.length}/{FEATURE_ORDER.length}
                </span>
              </span>
              <Crown size={14} className="text-yellow-400" />
            </div>
            <div className="glass-card-premium px-6 py-3.5 flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-sm text-zinc-300">
                {competitor.name}:{' '}
                <span className="font-bold text-white">
                  {compFullFeatures}/{FEATURE_ORDER.length}
                </span>
              </span>
            </div>
          </div>

          {/* Table */}
          <div className="glass-card-premium overflow-hidden">
            <div className="overflow-x-auto">
              {/* Table Header */}
              <div className="grid grid-cols-3 gap-4 p-5 md:p-6 border-b border-white/[0.06] bg-white/[0.02] min-w-[500px]">
                <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                  Feature
                </div>
                <div className="text-xs font-semibold text-center uppercase tracking-wider">
                  <span className="text-red-400">BuilderLync</span>
                </div>
                <div className="text-xs font-semibold text-center text-zinc-500 uppercase tracking-wider">
                  {competitor.name}
                </div>
              </div>

              {/* Table Rows */}
              {FEATURE_ORDER.map((key, i) => {
                const blHas = BUILDERLYNC_FEATURES[key]
                const compHas = competitor.features[key]
                const isWin = blHas === true && compHas !== true

                return (
                  <div
                    key={key}
                    className={`grid grid-cols-3 gap-4 p-4 md:px-6 md:py-4 border-b border-white/[0.03] items-center min-w-[500px] transition-colors duration-300 ${
                      isWin
                        ? 'bg-green-500/[0.04] hover:bg-green-500/[0.07]'
                        : 'hover:bg-white/[0.02]'
                    }`}
                    style={{
                      opacity: tableInView ? 1 : 0,
                      transform: tableInView ? 'translateX(0)' : 'translateX(-20px)',
                      transition: `opacity 0.5s ease-out ${i * 0.04}s, transform 0.5s ease-out ${i * 0.04}s`,
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-zinc-300 font-medium">
                        {FEATURE_LABELS[key]}
                      </span>
                      {isWin && (
                        <span
                          className="anim-scale-in"
                          style={{
                            animationDelay: `${i * 0.04 + 0.3}s`,
                            animationFillMode: 'both',
                          }}
                        >
                          <Trophy size={12} className="text-yellow-400" />
                        </span>
                      )}
                    </div>
                    <div className="flex justify-center">
                      <FeatureIcon value={blHas} />
                    </div>
                    <div className="flex justify-center">
                      <FeatureIcon value={compHas} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-6 text-xs text-zinc-400">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-500/15">
                <Check size={12} className="text-green-400" />
              </span>
              Full support
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-yellow-500/15">
                <AlertCircle size={12} className="text-yellow-400" />
              </span>
              Limited
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500/10">
                <X size={12} className="text-red-400" />
              </span>
              Not available
            </div>
          </div>
        </div>
      </section>

      {/* ── Price Comparison ─────────────────────────────────── */}
      <section className="section" ref={priceRef}>
        <div className="section-inner">
          <div
            className={`anim-fade-in-up ${priceInView ? 'in-view' : ''} text-center mb-12`}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              Pricing{' '}
              <span className="gradient-text-red">Comparison</span>
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              See why BuilderLync delivers more value per dollar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
            {/* BuilderLync */}
            <div
              className={`anim-fade-in-left anim-delay-1 ${priceInView ? 'in-view' : ''} glass-card-premium p-8 md:p-10 border-red-600/30 relative overflow-hidden`}
            >
              {/* Background glow */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-red-600/10 rounded-full blur-[60px]" />

              <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl">
                BEST VALUE
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">BuilderLync</h3>
              <div className="space-y-4 mb-6">
                {[
                  { plan: 'Starter', price: '$497', note: 'The full operations platform, without AI' },
                  { plan: 'Pro', price: '$997', note: 'Adds Storm Intelligence and the full AI stack' },
                ].map((tier) => (
                  <div key={tier.plan}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-extrabold text-white">{tier.price}</span>
                      <span className="text-lg font-normal text-zinc-400">/mo</span>
                      <span className="ml-1 text-xs font-semibold uppercase tracking-widest text-red-500">{tier.plan}</span>
                    </div>
                    <p className="text-sm text-zinc-400">{tier.note}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-zinc-400 mb-8">
                Flat pricing — unlimited users on every plan
              </p>
              <ul className="space-y-3">
                {[
                  'Unlimited users & seats, no per-user fees',
                  'Pro includes 10,000 AI credits + 500 AI voice minutes / mo',
                  'No contracts, month-to-month or annual',
                  'Enterprise plans available for multi-org teams',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-zinc-300">
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500/15">
                      <Check size={12} className="text-green-400" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Competitor */}
            <div
              className={`anim-fade-in-right anim-delay-2 ${priceInView ? 'in-view' : ''} glass-card-premium p-8 md:p-10`}
            >
              <h3 className="text-2xl font-bold text-white mb-2">
                {competitor.name}
              </h3>
              <div className="text-4xl font-extrabold text-white mb-1">
                {competitor.price}
              </div>
              <p className="text-sm text-zinc-400 mb-8">{competitor.priceNote}</p>

              <div className="mb-6">
                <h4 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-3">
                  Pros
                </h4>
                <ul className="space-y-2">
                  {competitor.pros.map((pro) => (
                    <li key={pro} className="flex items-center gap-3 text-sm text-zinc-300">
                      <Check size={16} className="text-green-400 flex-shrink-0" />
                      {pro}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-3">
                  Cons
                </h4>
                <ul className="space-y-2">
                  {competitor.cons.map((con) => (
                    <li key={con} className="flex items-center gap-3 text-sm text-zinc-300">
                      <X size={16} className="text-red-400 flex-shrink-0" />
                      {con}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Switch — Alternating Layouts ──────────────────── */}
      <section className="section" ref={whyRef}>
        <div className="section-inner">
          <div
            className={`anim-fade-in-up ${whyInView ? 'in-view' : ''} text-center mb-16`}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              Why Switch to{' '}
              <span className="gradient-text-red">BuilderLync</span>
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              {blWins} reasons contractors are switching from {competitor.name}.
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-8 lg:space-y-12">
            {competitor.whySwitch.map((reason, i) => {
              const Icon = whySwitchIcons[i % whySwitchIcons.length]
              const isEven = i % 2 === 0

              return (
                <div
                  key={reason.title}
                  className={`glass-card-premium p-8 md:p-10 anim-fade-in-up anim-delay-${Math.min(i + 1, 8)} ${whyInView ? 'in-view' : ''}`}
                >
                  <div
                    className={`flex flex-col ${
                      isEven ? 'md:flex-row' : 'md:flex-row-reverse'
                    } items-center gap-8 md:gap-12`}
                  >
                    {/* Icon side */}
                    <div className="flex-shrink-0">
                      <div
                        className="w-20 h-20 md:w-24 md:h-24 rounded-3xl bg-gradient-to-br from-red-600/20 to-red-600/5 flex items-center justify-center ring-1 ring-red-600/20 anim-scale-in in-view"
                      >
                        <Icon size={36} className="text-red-500" />
                      </div>
                    </div>

                    {/* Content side */}
                    <div className={`text-center ${isEven ? 'md:text-left' : 'md:text-right'} flex-1`}>
                      <div className="flex items-center gap-2 mb-2 justify-center md:justify-start">
                        <span className="text-xs font-bold text-red-500/60 uppercase tracking-wider">
                          Reason {i + 1}
                        </span>
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                        {reason.title}
                      </h3>
                      <p className="text-zinc-400 leading-relaxed max-w-lg">
                        {reason.description}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section className="section">
        <div className="section-inner max-w-3xl" ref={faqRef}>
          <h2
            className={`anim-fade-in-up ${faqInView ? 'in-view' : ''} text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-10`}
          >
            Frequently Asked{' '}
            <span className="gradient-text-red">Questions</span>
          </h2>

          <div className="space-y-3">
            {competitor.faqs.map((faq, i) => (
              <FAQItem key={i} q={faq.question} a={faq.answer} index={i} />
            ))}
          </div>
        </div>
      </section>

      <RelatedGuides
        badge="Explore"
        cta="Learn more"
        heading="Keep Researching"
        subheading="The in-depth review, other comparisons and BuilderLync pricing."
        guides={compareLinks(competitor.slug)}
      />

      {/* ── Bottom CTA ───────────────────────────────────────── */}
      <section className="section">
        <div className="section-inner">
          <div
            ref={ctaRef}
            className={`anim-fade-in-up ${ctaInView ? 'in-view' : ''} glass-card-premium p-10 sm:p-16 text-center max-w-3xl mx-auto relative overflow-hidden`}
          >
            {/* Background glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[200px] bg-red-600/10 rounded-full blur-[80px]" />

            <div className="relative z-10">
              <div
                className="w-16 h-16 rounded-2xl bg-red-600/10 flex items-center justify-center mx-auto mb-6 anim-scale-in in-view"
              >
                <TrendingUp size={28} className="text-red-500" />
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
                See Why Contractors Are Switching to{' '}
                <span className="gradient-text-red">BuilderLync</span>
              </h2>
              <p className="text-zinc-400 mb-8 max-w-lg mx-auto">
                Replace {competitor.name} and {blWins - 1}+ other tools with one
                all-in-one platform. Book a free demo today.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <AttributedCtaLink
                  href={BOOK_A_DEMO_URL}
                  page="compare"
                  cta={`Bottom CTA — Book a Free Demo (${competitor.name})`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-base px-8 py-4 glow-red-sm"
                >
                  Book a Free Demo
                  <ArrowRight size={18} />
                </AttributedCtaLink>
                <Link href="/pricing" className="btn-ghost text-base px-7 py-3.5">
                  View Pricing
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
